import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Alert, Icon, Link } from '@zapier/design-system';
import { formatResponseDataToTaskBannerEligibility, getTaskAlertBannerEligibility, TASK_ALERT_BANNERS, } from './utils';
import { hasDismissedBannerBetweenDates } from './utils/hasDismissedBannerBetweenDates';
import { useUniversalNavContext } from '../../../../universal-nav/hooks';
import { emitFullStoryEvent } from '../../../../universal-nav/utils/fullStoryEvents';
import { UserInteractionEvent } from '../../../../universal-nav/utils/avroEvents';
import { Fragment, useEffect, useState } from 'react';
import { baseReset, boxSizing } from '../../../../utils/style-mixins/reset';
export const shouldRender = (data) => {
    var _a;
    const { currentAccount } = data;
    const isOwnerOrAdminOrSuperAdmin = (currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.currentUserRole) &&
        ['owner', 'admin', 'super_admin'].includes(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.currentUserRole);
    if (!isOwnerOrAdminOrSuperAdmin)
        return false;
    const isUnlimitedAccount = (_a = data.currentAccount) === null || _a === void 0 ? void 0 : _a.isUnlimited;
    if (isUnlimitedAccount)
        return false;
    const periodStartDate = currentAccount.periodStartDate;
    const periodEndDate = currentAccount.periodEndDate;
    const banner = getTaskAlertBannerEligibility(formatResponseDataToTaskBannerEligibility(data));
    if (!banner) {
        return false;
    }
    const hasDismissedTargetBannerThisPeriod = hasDismissedBannerBetweenDates({
        dismissedOn: localStorage.getItem(TASK_ALERT_BANNERS[banner].dismissalKey),
        startDate: periodStartDate,
        endDate: periodEndDate,
    });
    return !hasDismissedTargetBannerThisPeriod;
};
const Styles = labelStyles('TaskUsageAlertBanner', {
    icon: css(Object.assign(Object.assign({}, boxSizing), { display: 'inline-block', verticalAlign: 'middle', marginRight: '10px', position: 'relative' })),
    strong: css(Object.assign(Object.assign({}, boxSizing), { font: `var(--zds-body-4)` })),
    text: css(Object.assign({}, baseReset)),
});
const useTaskUsageBanner = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { getFullyQualifiedURL } = useUniversalNavContext();
    const banner = getTaskAlertBannerEligibility(formatResponseDataToTaskBannerEligibility(props.data));
    const [dismissedOn, setDismissedOn] = useState(banner
        ? localStorage.getItem(TASK_ALERT_BANNERS[banner].dismissalKey)
        : null);
    const periodStartDate = props.data.currentAccount.periodStartDate;
    const periodEndDate = props.data.currentAccount.periodEndDate;
    const hasDismissedTargetBannerThisPeriod = hasDismissedBannerBetweenDates({
        dismissedOn,
        startDate: periodStartDate,
        endDate: periodEndDate,
    });
    useEffect(() => {
        if (!banner || hasDismissedTargetBannerThisPeriod)
            return;
        const eventOptions = {
            interaction_name: 'task_alert_banner_rendered',
            event_action: 'in_view',
            interaction_goal: 'not_applicable',
            option_selected: 'not_applicable',
        };
        props.trackEvent(UserInteractionEvent, eventOptions);
        emitFullStoryEvent(eventOptions);
    }, [hasDismissedTargetBannerThisPeriod, banner]);
    useEffect(() => {
        if (!banner || dismissedOn === null)
            return;
        try {
            localStorage.setItem(TASK_ALERT_BANNERS[banner].dismissalKey, JSON.stringify(dismissedOn));
        }
        catch (_a) {
            console.log('Failed to save task alert banner dismissal to localStorage');
        }
    }, [banner, dismissedOn]);
    if (hasDismissedTargetBannerThisPeriod || !banner) {
        return {
            component: null,
        };
    }
    const onDismiss = () => {
        setDismissedOn(new Date().toUTCString());
        const eventOptions = {
            interaction_name: 'task_alert_banner_dismissed',
            event_action: 'click',
            interaction_goal: 'dismiss_banner',
            option_selected: 'not_applicable',
        };
        props.trackEvent(UserInteractionEvent, eventOptions);
        emitFullStoryEvent(eventOptions);
    };
    const upgradeURL = getFullyQualifiedURL(((_a = props.data.nextPlan) === null || _a === void 0 ? void 0 : _a.id)
        ? `/app/planbuilder/plans?planId=${(_b = props.data.nextPlan) === null || _b === void 0 ? void 0 : _b.id}`
        : `/app/planbuilder/plans`);
    const billingSettingsURL = getFullyQualifiedURL(encodeURI(`/app/settings/${props.data.currentAccount.id}/billing`));
    const contactSalesURL = 'https://zapier.com/l/contact-sales?demo_source=EntSKU-logged-in_task-limit-alert';
    const onLinkClick = (e, interactionGoal) => {
        var _a, _b, _c;
        const eventOptions = {
            interaction_name: 'task_alert_banner_cta_clicked',
            interaction_raw_text: (_b = (_a = e.currentTarget) === null || _a === void 0 ? void 0 : _a.textContent) !== null && _b !== void 0 ? _b : '',
            destination_url_address: (_c = e.currentTarget.getAttribute('href')) !== null && _c !== void 0 ? _c : '',
            interaction_goal: interactionGoal,
            option_selected: 'not_applicable',
            event_action: 'click',
        };
        props.trackEvent(UserInteractionEvent, eventOptions);
        emitFullStoryEvent(eventOptions);
    };
    const upgradeLink = (text) => (jsx(Link, { href: upgradeURL, onClick: (e) => onLinkClick(e, 'upgrade_plan') }, text));
    const manageLink = (text) => (jsx(Link, { href: billingSettingsURL, onClick: (e) => onLinkClick(e, 'manage_plan') }, text));
    const contactSalesLink = (text) => (jsx(Link, { href: contactSalesURL, onClick: (e) => onLinkClick(e, 'contact_sales') }, text));
    const alertText = (primaryText, secondaryText, iconName) => (jsx(Fragment, null,
        jsx("strong", { css: Styles.strong },
            iconName && (jsx("span", { css: Styles.icon },
                jsx(Icon, { name: iconName, size: 24 }))),
            primaryText),
        secondaryText));
    const banners = {
        freeAccount: {
            [TASK_ALERT_BANNERS.lowTasks.banner]: (jsx(Alert, { variant: props.variant, status: "warning", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText("You're running low on tasks. ", 'To prevent your Zaps from being paused, '),
                    upgradeLink('update your plan now.')))),
            [TASK_ALERT_BANNERS.outOfTasks.banner]: (jsx(Alert, { variant: props.variant, status: "error", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText('Your Zaps are paused. ', 'To turn them back on, upgrade your plan to '),
                    upgradeLink('get more tasks.')))),
        },
        individualAccount: {
            [TASK_ALERT_BANNERS.lowTasks.banner]: (jsx(Alert, { variant: props.variant, status: "warning", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText("You're running low on tasks. ", 'To prevent your Zaps from being paused, '),
                    upgradeLink('get more tasks.')))),
            [TASK_ALERT_BANNERS.outOfTasks.banner]: (jsx(Alert, { variant: props.variant, status: "error", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText('Your Zaps are paused. ', 'To turn them back on, '),
                    upgradeLink('get more tasks.')))),
            [TASK_ALERT_BANNERS.aboutToEnterMeteredBilling.banner]: (jsx(Alert, { variant: props.variant, status: "info", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText("You're nearing your plan's task limit. ", 'Pay-per-task billing will activate so your Zaps keep running or you can '),
                    manageLink('manage your plan.')))),
            [TASK_ALERT_BANNERS.enteredMeteredBilling.banner]: (jsx(Alert, { variant: props.variant, status: "info", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText("You've reached your plan's task limit. ", 'Pay-per-task billing will keep your Zaps running or you can '),
                    manageLink('manage your plan.')))),
        },
        teamAccount: {
            [TASK_ALERT_BANNERS.lowTasks.banner]: (jsx(Alert, { variant: props.variant, status: "warning", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText('Your account is running low on tasks. ', 'To prevent your Zaps from being paused, '),
                    upgradeLink('get more tasks.')))),
            [TASK_ALERT_BANNERS.outOfTasks.banner]: (jsx(Alert, { variant: props.variant, status: "error", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText("Your Account's Zaps are paused. ", 'To turn them back on, '),
                    upgradeLink('get more tasks.')))),
            [TASK_ALERT_BANNERS.aboutToEnterMeteredBilling.banner]: (jsx(Alert, { variant: props.variant, status: "info", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText("You're nearing your plan's task limit. ", 'Pay-per-task billing will activate so your Zaps keep running or you can '),
                    manageLink('manage your plan.')))),
            [TASK_ALERT_BANNERS.enteredMeteredBilling.banner]: (jsx(Alert, { variant: props.variant, status: "info", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText("You've reached your plan's task limit. ", 'Pay-per-task billing will keep your Zaps running or you can '),
                    manageLink('manage your plan.')))),
        },
        salesManagedAccount: {
            [TASK_ALERT_BANNERS.lowTasks.banner]: (jsx(Alert, { variant: props.variant, status: "warning", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText('Your account is running low on tasks. ', 'To prevent your Zaps from being paused, please '),
                    contactSalesLink('contact sales.')))),
            [TASK_ALERT_BANNERS.outOfTasks.banner]: (jsx(Alert, { variant: props.variant, status: "error", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText("Your Account's Zaps are paused. ", 'To turn them back on, please '),
                    contactSalesLink('contact sales.')))),
            [TASK_ALERT_BANNERS.aboutToEnterMeteredBilling.banner]: (jsx(Alert, { variant: props.variant, status: "info", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText("You're nearing your plan's task limit. ", 'Pay-per-task billing will activate so your Zaps keep running. '),
                    contactSalesLink('Contact sales'),
                    " to manage your plan."))),
            [TASK_ALERT_BANNERS.enteredMeteredBilling.banner]: (jsx(Alert, { variant: props.variant, status: "info", onDismiss: onDismiss },
                jsx("p", { "data-zds": true, css: Styles.text },
                    alertText("You've reached your plan's task limit. ", 'Pay-per-task billing will keep your Zaps running or to manage your plan, '),
                    contactSalesLink('contact sales.')))),
        },
    };
    if (((_d = (_c = props.data.currentAccount.plan) === null || _c === void 0 ? void 0 : _c.limits) === null || _d === void 0 ? void 0 : _d.isSelfServe) === false) {
        return {
            component: (_e = banners.salesManagedAccount[banner]) !== null && _e !== void 0 ? _e : null,
        };
    }
    if (props.data.currentAccount.isTeamAccount) {
        return {
            component: (_f = banners.teamAccount[banner]) !== null && _f !== void 0 ? _f : null,
        };
    }
    if (props.data.currentAccount.plan.amount === 0) {
        return {
            component: (_g = banners.freeAccount[banner]) !== null && _g !== void 0 ? _g : null,
        };
    }
    return {
        component: (_h = banners.individualAccount[banner]) !== null && _h !== void 0 ? _h : null,
    };
};
export const Component = function TaskUsage(props) {
    const { component } = useTaskUsageBanner(props);
    if (!component)
        return null;
    return component;
};
//# sourceMappingURL=TaskUsage.js.map
// @zapier/universal-layout@12.0.37 (fb08e5a7f)
